import { useEffect, useMemo, useRef, useState } from 'react';

export const useSelectInput = ({
  initialSelectedItems = [],
  options = [],
  isSearchable = false,
  isResetClicked = false,
  onReset,
  onRemove,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState(initialSelectedItems);
  const rootRef = useRef(null);
  const prevInitialRef = useRef(initialSelectedItems);

  // Sync only when initialSelectedItems meaningfully changes:
  // - gains items (edit mode pre-fill, context switch with cities)
  // - explicitly cleared to [] after having items (context switch to empty request)
  useEffect(() => {
    const prev = prevInitialRef.current;
    const curr = initialSelectedItems;
    const hadItems = prev.length > 0;
    const hasItems = curr.length > 0;
    if (hasItems || hadItems) {
      setSelectedItems(curr);
    }
    prevInitialRef.current = curr;
  }, [initialSelectedItems]);

  const onRemoveRef = useRef(onRemove);
  onRemoveRef.current = onRemove;
  const prevResetRef = useRef(false);

  useEffect(() => {
    if (isResetClicked && !prevResetRef.current) {
      setSelectedItems([]);
      setSearchQuery('');
      onRemoveRef.current?.([]);
      onReset?.();
    }
    prevResetRef.current = isResetClicked;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResetClicked, onReset]);

  useEffect(() => {
    const handler = e => {
      if (!rootRef.current?.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const closeDropdown = () => {
    setIsOpen(false);
    if (isSearchable) setSearchQuery('');
  };

  const toggleOpen = () => setIsOpen(prev => !prev);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(o => o.title?.toLowerCase().includes(q));
  }, [options, searchQuery]);

  const isSelected = item =>
    selectedItems.some(
      s =>
        s.value === item.value ||
        (item.id != null && s.id === item.id)
    );

  const toggleItem = item => {
    if (isSelected(item)) {
      return selectedItems.filter(
        s => s.value !== item.value && !(item.id != null && s.id === item.id)
      );
    }
    return [...selectedItems, item];
  };

  return {
    isOpen,
    toggleOpen,
    closeDropdown,
    searchQuery,
    setSearchQuery,
    selectedItems,
    setSelectedItems,
    filteredOptions,
    isSelected,
    toggleItem,
    rootRef,
  };
};
